import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Typography } from '@mui/material';

interface TwoFactorAuthProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  isLoading?: boolean;
  errorMessage?: string | null;
  successMessage?: string | null;
}

const TwoFactorAuth: React.FC<TwoFactorAuthProps> = ({ 
  email,
  onVerify,
  onResend,
  onBack,
  isLoading = false,
  errorMessage = null,
  successMessage = null
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [hasCodeError, setCodeError] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);

  React.useEffect(() => {
    if (resendCooldown > 0) {
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, []);

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    setCodeError(false);
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setCodeError(true);
      return;
    }
    await onVerify(verificationCode);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    
    await onResend();
    
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleVerify();
    }
  };

  // Check if the error message indicates an expired code
  const isExpiredCode = errorMessage && (
    errorMessage.toLowerCase().includes('expired') || 
    errorMessage.toLowerCase().includes('expiry')
  );

  // Check if the error message indicates an invalid code
  const isInvalidCode = errorMessage && (
    errorMessage.toLowerCase().includes('invalid') || 
    errorMessage.toLowerCase().includes('incorrect')
  );

  return (
    <Stack spacing={2} sx={{ marginTop: 3, textAlign: 'center' }}>
      <Typography variant="h6">Enter Verification Code</Typography>
      
      <Typography variant="body2" color="text.secondary">
        We've sent a 6-digit code to <strong>{email}</strong>
      </Typography>

      {successMessage && (
        <Alert severity="success">{successMessage}</Alert>
      )}

      {errorMessage && (
        <Alert severity="error">
          {errorMessage}
          {isExpiredCode && (
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2">
                Your verification code has expired. Please request a new one.
              </Typography>
            </div>
          )}
          {isInvalidCode && (
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2">
                Please check your code and try again.
              </Typography>
            </div>
          )}
        </Alert>
      )}

      <FormControl error={hasCodeError}>
        <TextField
          value={verificationCode}
          onChange={onCodeChange}
          label="6-digit code"
          onKeyPress={onKeyPress}
          inputProps={{
            style: { 
              textAlign: 'center', 
              fontSize: '1.5rem', 
              letterSpacing: '0.5rem',
              fontFamily: 'monospace'
            },
            maxLength: 6
          }}
          placeholder="000000"
        />
        {hasCodeError && (
          <FormHelperText>Please enter a 6-digit code</FormHelperText>
        )}
      </FormControl>

      <Button 
        variant="contained" 
        onClick={handleVerify}
        disabled={verificationCode.length !== 6 || isLoading}
      >
        Verify Code
      </Button>

      <Button
        variant="outlined"
        onClick={handleResend}
        disabled={resendCooldown > 0 || isLoading}
      >
        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
      </Button>

      <Button variant="text" onClick={onBack}>
        ← Back to Login
      </Button>
    </Stack>
  );
};

export default TwoFactorAuth;
